import { Key, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MdOutlineEdit, MdOutlineMoreHoriz } from 'react-icons/md';
import { Chip, TmdxAvatar } from '@ds';
import { ActionMenu, ConditionalRender, MenuItemType, TableColumnType, TruncatedListWrapper } from '@ui';
import { RegionHub, RegionType } from '@/api/apis/facilities';
import { getFullName } from '@/utils/helpers';
import { Employee } from '@/api/apis/resources';
import { col, COMMON_COLUMN_PRESETS } from '../../utils';

export const REGION_TABLE_COLUMNS: TableColumnType[] = [
  col('name', 'Region name', {
    sortable: true,
    minWidthClassName: 'min-w-[160px]',
  }),
  col('regionalManager', 'Regional manager', {
    sortable: false,
    minWidthClassName: 'min-w-[160px]',
  }),
  col('nationalManager', 'National manager', {
    sortable: false,
    minWidthClassName: 'min-w-[160px]',
  }),
  col('hubs', 'HUBs', {
    sortable: false,
    minWidthClassName: 'min-w-[280px]',
  }),
  COMMON_COLUMN_PRESETS.actions,
];

const getActionsMenu = (onEdit: () => void): MenuItemType[] => [
  {
    key: 'edit-region',
    type: 'action',
    to: 'edit-region',
    label: (
      <div className="flex items-center gap-2">
        <MdOutlineEdit size={16} />
        <span className="text-m-r">Edit</span>
      </div>
    ),
    onAction: () => onEdit(),
  },
];

const getEmployeesCell = (employees: Employee[] | null) => {
  return (
    <ConditionalRender show={!!employees?.length} placeholder="-">
      <TruncatedListWrapper wrapperClassName="flex items-center gap-2 max-w-[340px]">
        {employees?.map((employee, i) => {
          const fullName = getFullName(employee?.firstName, employee?.lastName);

          return (
            <div key={employee.id} className="flex items-center gap-2 whitespace-nowrap">
              <TmdxAvatar radius="md" size="md" name={fullName} src={employee?.profilePhoto} />
              <span className="text-m-m">
                {fullName}
                {i < employees.length - 1 ? ',' : ''}
              </span>
            </div>
          );
        })}
      </TruncatedListWrapper>
    </ConditionalRender>
  );
};

const getHubsCell = (hubs: RegionHub[] | null) => {
  return (
    <ConditionalRender show={!!hubs?.length} placeholder="-">
      <TruncatedListWrapper wrapperClassName="flex items-center gap-2 max-w-[280px]">
        {hubs?.map((hub) => (
          <Chip key={hub.id} size="sm">
            <span className="text-m-m text-base-5">{hub.name}</span>
          </Chip>
        ))}
      </TruncatedListWrapper>
    </ConditionalRender>
  );
};

export const getRenderRegionTableCell =
  (onEdit: (regionId: string) => void) =>
  (region: RegionType, columnKey: Key): ReactNode => {
    const { id, name, regionalManagers, nationalManagers, hubs } = region;
    const ACTIONS_MENU = getActionsMenu(() => onEdit(id));

    switch (columnKey) {
      case 'name':
        return (
          <Link to={`/admin/regions/${id}`} className="flex items-center gap-2">
            <span className="text-m-m">{name}</span>
          </Link>
        );
      case 'regionalManager':
        return getEmployeesCell(regionalManagers);
      case 'nationalManager':
        return getEmployeesCell(nationalManagers);
      case 'hubs':
        return getHubsCell(hubs);
      case 'actions':
        return (
          <ActionMenu
            placement="left-start"
            items={ACTIONS_MENU}
            MenuTrigger={
              <div>
                <MdOutlineMoreHoriz size={20} />
              </div>
            }
            label={`${name} actions menu`}
          />
        );
      default:
        return null;
    }
  };
